import { createStore } from "vuex";
import api from "@/utils/api";
import jwt_decode from "jwt-decode";
import createPersistedState from "vuex-persistedstate";

export default createStore({
  state: {
    websiteDetails: null,
    websiteLoading: false,
    websiteSaving: false,
    studentAttendanceYearly: {},
    teacherAttendanceYearly: {},
    userLeaveBalances: {},
    leaveBalance: null,
    announcements: [],
    announcementsUnread: 0,
    announcementsLoading: false,

    smartCards: [],
    smartCardsTotal: 0,
    cardsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 1,
    },
    smartCardStats: {
      totalCards: 0,
      totalTeacherCards: 0,
      totalStudentCards: 0,
      totalInTransit: 0,
      totalActive: 0,
      totalDisabled: 0,
    },
    schoolsBasic: [],

    allDevices: [],
    allDevicesTotal: 0,
    devices: [],
    devicesTotal: 0,

    leaveRequests: [],
    leaveSummary: {
      totalLeaves: 0,
      totalApproved: 0,
      totalPending: 0,
      totalDeclined: 0,
    },

    isInitialized: false,
    classes: [],
    classesTotal: 0,
    notifications: [],
    attendanceRecords: [],
    attendanceTotal: 0,
    currentUserDetails: null,
    teachers: [],

    teacherPagination: {
      totalPages: 0,
      totalResults: 0,
      currentPage: 1,
    },

    students: [],
    schools: [],
    upcomingEvents: [],
    userRole: null,
    userName: null,
    userId: null,
    schoolId: null,
    userPhoto: null,

    attendanceHistory: [],
    todaysAttendancePercentage: null,
    attendanceStats: {
      daily: [],
      today: {
        present: 0,
        absent: 0,
        leave: 0,
        total: 0,
      },
    },

    userCache: {},
    currentUser: null,
    attendancePagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 1,
    },
    studentPagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 1,
    },
    studentTotalCount: 0,
    onboardingTotalStudents: 0,
    onboardingTotalTeachers: 0,
  },

  getters: {
    getWebsiteDetails: (state) => state.websiteDetails,
    getWebsiteLoading: (state) => state.websiteLoading,
    getWebsiteSaving: (state) => state.websiteSaving,
    getLeaveBalance: (state) => state.leaveBalance,
    getAnnouncements: (state) => state.announcements,
    getPinnedAnnouncements: (state) =>
      state.announcements.filter((a) => a.isPinned),

    getNormalAnnouncements: (state) =>
      state.announcements.filter((a) => !a.isPinned),

    getUnreadAnnouncementsCount: (state) => state.announcementsUnread,

    getSmartCards: (state) => state.smartCards,
    getSmartCardsTotal: (state) => state.smartCardsTotal,

    getDevices: (state) => state.devices,
    getDevicesTotal: (state) => state.devicesTotal,

    getLeaveRequests: (state) => state.leaveRequests,
    getLeaveSummary: (state) => state.leaveSummary,
    totalClasses: (state) => state.classesTotal,

    allClasses: (state) => state.classes,
    currentUserDetails: (state) => state.currentUserDetails,
    getTotalTeachers: (state) => state.teacherPagination.totalResults,
    getTeacherPagination: (state) => state.teacherPagination,
    getAllTeachers: (state) => state.teachers || [],

    getUpcomingEvents: (state) => state.upcomingEvents,
    allStudents: (state) => state.students,
    getUserRole: (state) => state.userRole,
    getUserPhoto: (state) => state.userPhoto,
    isTeacher: (state) => state.userRole === "teacher",
    isStudent: (state) => state.userRole === "student",
    getUserName: (state) => state.userName,
    getUserId: (state) => state.userId,
    getAttendanceHistory: (state) => state.attendanceHistory,
    getAttendanceStats: (state) => state.attendanceStats,
    getUserFromCache: (state) => (id) => state.userCache[id],
    getAttendancePagination: (state) => state.attendancePagination,
    getTotalAttendanceResults: (state) => state.attendancePagination.total,
    getStudentPagination: (state) => state.studentPagination,
    getTotalStudents: (state) => state.studentPagination.total,
    getTotalStudentCount: (state) => state.studentTotalCount,
    getSchools: (state) => state.schools,
    isAuthenticated: (state) =>
      !!state.userId && !!localStorage.getItem("token"),
    isOnboardingCompleted: (state) => {
      return (
        state.onboardingCompleted ||
        (state.user?.schoolId &&
          state.classes?.length > 1 &&
          state.teachers?.length > 0 &&
          state.students?.length > 0)
      );
    },
    getOnboardingTotalStudents: (state) => state.onboardingTotalStudents,
    getOnboardingTotalTeachers: (state) => state.onboardingTotalTeachers,
  },

  mutations: {
    SET_WEBSITE_DETAILS(state, payload) {
      state.websiteDetails = payload;
    },

    SET_WEBSITE_LOADING(state, value) {
      state.websiteLoading = value;
    },

    SET_WEBSITE_SAVING(state, value) {
      state.websiteSaving = value;
    },
    SET_STUDENT_YEARLY_ATTENDANCE(state, { userId, rangeKey, data }) {
      if (!state.studentAttendanceYearly) {
        state.studentAttendanceYearly = {};
      }

      if (!state.studentAttendanceYearly[userId]) {
        state.studentAttendanceYearly[userId] = {};
      }

      state.studentAttendanceYearly[userId][rangeKey] = data;
    },
    SET_TEACHER_YEARLY_ATTENDANCE(state, { userId, rangeKey, data }) {
      if (!state.teacherAttendanceYearly[userId]) {
        state.teacherAttendanceYearly[userId] = {};
      }

      state.teacherAttendanceYearly[userId][rangeKey] = data;
    },
    SET_USER_LEAVE_BALANCE(state, { userId, year, balance }) {
      if (!state.userLeaveBalances[userId]) {
        state.userLeaveBalances[userId] = {};
      }

      state.userLeaveBalances[userId][year] = balance;
    },
    SET_LEAVE_BALANCE(state, balance) {
      state.leaveBalance = balance;
    },
    SET_CARDS_PAGINATION(state, pagination) {
      state.cardsPagination = pagination;
    },
    SET_ANNOUNCEMENTS(state, announcements) {
      state.announcements = announcements || [];
    },

    ADD_ANNOUNCEMENT(state, announcement) {
      state.announcements.unshift(announcement);
    },

    UPDATE_ANNOUNCEMENT(state, updated) {
      const index = state.announcements.findIndex((a) => a._id === updated._id);
      if (index !== -1) state.announcements.splice(index, 1, updated);
    },

    DELETE_ANNOUNCEMENT(state, id) {
      state.announcements = state.announcements.filter((a) => a._id !== id);
    },

    MARK_ANNOUNCEMENT_READ(state, id) {
      const ann = state.announcements.find((a) => a._id === id);
      if (!ann) return;

      if (!ann.readBy) ann.readBy = [];

      ann.readBy.push({
        userId: state.userId,
        readAt: new Date(),
      });
    },

    SET_ANNOUNCEMENTS_LOADING(state, value) {
      state.announcementsLoading = value;
    },

    SET_SMART_CARDS(state, cards) {
      state.smartCards = cards;
    },

    SET_SMART_CARDS_TOTAL(state, total) {
      state.smartCardsTotal = total;
    },

    SET_SMARTCARD_STATS(state, stats) {
      state.smartCardStats = stats;
    },
    SET_SCHOOLS_BASIC(state, payload) {
      state.schoolsBasic = payload;
    },
    SET_ALL_DEVICES(state, devices) {
      state.allDevices = devices;
    },
    SET_ALL_DEVICES_TOTAL(state, total) {
      state.allDevicesTotal = total;
    },
    SET_DEVICES(state, devices) {
      state.devices = devices;
    },
    SET_DEVICES_TOTAL(state, total) {
      state.devicesTotal = total;
    },

    SET_LEAVE_REQUESTS(state, leaves) {
      state.leaveRequests = leaves;
    },
    SET_LEAVE_SUMMARY(state, summary) {
      state.leaveSummary = summary;
    },
    SET_INITIALIZED(state, value) {
      state.isInitialized = value;
    },
    SET_CLASSES(state, payload) {
      state.classes = payload.classes || [];
      state.classesTotal = payload.total || 0;
    },

    SET_ATTENDANCE_DATA(state, payload) {
      state.attendanceRecords = payload.records;
      state.attendanceTotal = payload.total;
      state.attendancePagination.page = payload.page;
      state.attendancePagination.limit = payload.limit;
    },
    SET_ATTENDANCE_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications;
    },
    SET_CURRENT_USER_DETAILS(state, user) {
      state.currentUserDetails = user;
    },
    SET_TEACHER_PAGINATION(state, pagination) {
      state.teacherPagination = pagination;
    },
    SET_TEACHERS(state, teachers) {
      state.teachers = teachers;
    },
    SET_SCHOOLS(state, schools) {
      state.schools = schools;
    },
    SET_SCHOOL_ID(state, schoolId) {
      state.schoolId = schoolId;
      if (state.user) {
        state.user.schoolId = schoolId;
      } else {
        state.user = { schoolId };
      }
    },
    SET_TODAYS_ATTENDANCE_PERCENTAGE(state, payload) {
      state.todaysAttendancePercentage = payload;
    },
    setUpcomingEvents(state, events) {
      state.upcomingEvents = events;
    },
    SET_ATTENDANCE_PAGINATION(state, pagination) {
      state.attendancePagination = pagination;
    },

    SET_STUDENTS(state, students) {
      state.students = students;
    },
    SET_USER_ROLE(state, role) {
      state.userRole = role;
    },
    SET_USER_NAME(state, name) {
      state.userName = name;
    },
    SET_USER_ID(state, id) {
      state.userId = id;
    },
    SET_USER_PHOTO(state, photo) {
      state.userPhoto = photo;
    },
    SET_ATTENDANCE_HISTORY(state, history) {
      state.attendanceHistory = history;
    },
    SET_ATTENDANCE_STATS(state, stats) {
      state.attendanceStats = stats;
    },
    SET_USER_CACHE(state, { userId, user }) {
      state.userCache = {
        ...state.userCache,
        [userId]: user,
      };
    },
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;
    },
    SET_STUDENT_PAGINATION(state, pagination) {
      state.studentPagination = pagination;
    },
    SET_TOTAL_STUDENT_COUNT(state, total) {
      state.studentTotalCount = total;
    },
    SET_ONBOARDING_COMPLETED(state, completed) {
      state.onboardingCompleted = completed;
    },
    SET_ONBOARDING_TOTAL_STUDENTS(state, total) {
      state.onboardingTotalStudents = total;
    },
    SET_ONBOARDING_TOTAL_TEACHERS(state, total) {
      state.onboardingTotalTeachers = total;
    },
  },
  plugins: [
    createPersistedState({
      key: "edu-manage",
      storage: window.localStorage,
      reducer: (state) => {
        const baseState = {
          userRole: state.userRole,
          userName: state.userName,
          userId: state.userId,
          userPhoto: state.userPhoto,
        };

        if (state.userRole === "admin") {
          return {
            ...baseState,
            schoolId: state.schoolId,
            classes: state.classes,
            classesTotal: state.classesTotal,
          };
        }

        return baseState;
      },
    }),
  ],

  actions: {
    /* ======================================
       CONNECT CUSTOM DOMAIN
    ====================================== */
    async connectCustomDomain({ dispatch }, domain) {
      try {
        const res = await api.post("/api/website/connect-domain", {
          domain,
        });

        console.log("✅ Custom domain connected:", res.data);

        await dispatch("fetchWebsiteDetails");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to connect domain:",
          err.response?.data || err.message,
        );

        throw err;
      }
    },

    /* ======================================
       CHECK SSL ELIGIBILITY
    ====================================== */
    async canIssueCert(_, domain) {
      try {
        const res = await api.get("/api/website/can-issue-cert", {
          params: {
            domain,
          },
        });

        return res.data;
      } catch (err) {
        console.error(
          "❌ SSL validation failed:",
          err.response?.data || err.message,
        );

        throw err;
      }
    },
    /* ======================================
       FETCH WEBSITE DETAILS
    ====================================== */
    async fetchWebsiteDetails({ commit }) {
      try {
        commit("SET_WEBSITE_LOADING", true);

        const res = await api.get("/api/website/details");

        commit("SET_WEBSITE_DETAILS", res.data.school);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch website details:",
          err.response?.data || err.message,
        );

        throw err;
      } finally {
        commit("SET_WEBSITE_LOADING", false);
      }
    },

    /* ======================================
       UPDATE WEBSITE DETAILS (VUEX ACTION)
    ====================================== */
    async updateWebsiteDetails({ commit }, payload) {
      try {
        commit("SET_WEBSITE_SAVING", true);

        const formData = new FormData();

        /* ======================================
           SCALAR FIELDS
        ====================================== */
        const scalarFields = [
          "schoolName",
          "schoolCode",
          "schoolType",
          "board",
          "establishmentYear",
          "foundedYear",
          "boardAffiliation",
          "affiliationNumber",
          "officeHours",
          "tagline",
          "description",
          "aboutSchool",
          "email",
          "primaryPhone",
          "secondaryPhone",
          "whatsappNumber",
          "address",
          "village",
          "city",
          "district",
          "state",
          "country",
          "pincode",
          "latitude",
          "longitude",
          "googleMapLink",
          "template",
          "websiteEnabled",
          "websiteStatus",
          "themeColor",
          "secondaryColor",
          "fontFamily",

          // Leadership - Principal
          "principalEyebrow",
          "principalTitle",
          "principalName",
          "principalDesignation",
          "principalQualification",
          "principalMessage",

          // Leadership - Chairman (Backward Compatibility)
          "chairmanEyebrow",
          "chairmanName",
          "chairmanDesignation",
          "chairmanMessage",

          // Leadership - Vice Principal (Backward Compatibility)
          "vicePrincipalEyebrow",
          "vicePrincipalName",
          "vicePrincipalDesignation",
          "vicePrincipalMessage",

          "facebook",
          "instagram",
          "youtube",
          "twitter",
          "linkedin",
          "telegram",
          "portalLink",
          "admissionLink",
          "appDownloadLink",
          "seoTitle",
          "seoDescription",
          "ctaTitle",
          "ctaDescription",
          "ctaButtonText",
          "ctaButtonLink",
          "footerDescription",
          "footerCopyright",
          "admissionOpen",
          "admissionSession",
          "googleAnalyticsId",
          "facebookPixelId",
        ];

        scalarFields.forEach((field) => {
          if (payload[field] !== undefined && payload[field] !== null) {
            formData.append(field, payload[field]);
          }
        });

        /* ======================================
           SINGLE IMAGES
        ====================================== */
        const singleImages = [
          "logo",
          "favicon",
          "heroImage",
          "principalPhoto",
          "chairmanPhoto",
          "vicePrincipalPhoto",
          "ogImage",
        ];

        singleImages.forEach((field) => {
          if (payload[field] instanceof File) {
            formData.append(field, payload[field]);
          } else if (typeof payload[field] === "string") {
            formData.append(field, payload[field]);
          }
        });

        /* ======================================
           DYNAMIC NAVIGATION MENU ITEMS (NAVITEMS)
        ====================================== */
        if (Array.isArray(payload.navItems)) {
          const navItems = payload.navItems.map((item, index) => ({
            _id: item._id,
            code: item.code || `P.0${index + 1}`,
            label: item.label || "",
            url: item.url || "#",
            enabled: item.enabled !== false,
            sortOrder: item.sortOrder !== undefined ? item.sortOrder : index,
          }));

          formData.append("navItems", JSON.stringify(navItems));
        }

        /* ======================================
           HERO HIGHLIGHTS
        ====================================== */
        if (Array.isArray(payload.heroHighlights)) {
          const heroHighlights = payload.heroHighlights.map((item) => ({
            _id: item._id,
            icon: item.icon || "fa-circle-check",
            text: item.text || "",
          }));

          formData.append("heroHighlights", JSON.stringify(heroHighlights));
        }

        /* ======================================
           HERO SLIDER SETTINGS
        ====================================== */
        if (payload.heroSliderSettings) {
          formData.append(
            "heroSliderSettings",
            JSON.stringify(payload.heroSliderSettings),
          );
        }

        /* ======================================
           HERO SLIDES
        ====================================== */
        if (Array.isArray(payload.heroSlides)) {
          const heroSlides = payload.heroSlides.map((slide) => {
            let imageUrl = typeof slide.image === "string" ? slide.image : "";
            let mobileImageUrl =
              typeof slide.mobileImage === "string" ? slide.mobileImage : "";

            return {
              _id: slide._id,
              badge: slide.badge || "",
              title: slide.title || "",
              description: slide.description || "",
              caption: slide.caption || "",
              buttonText: slide.buttonText || "",
              buttonLink: slide.buttonLink || "",
              buttonTarget: slide.buttonTarget || "_self",
              secondaryButtonText: slide.secondaryButtonText || "",
              secondaryButtonLink: slide.secondaryButtonLink || "",
              secondaryButtonTarget: slide.secondaryButtonTarget || "_self",
              overlayOpacity:
                slide.overlayOpacity !== undefined ? slide.overlayOpacity : 0.4,
              contentAlignment: slide.contentAlignment || "left",
              sortOrder: slide.sortOrder !== undefined ? slide.sortOrder : 0,
              active: slide.active !== undefined ? slide.active : true,
              image: imageUrl,
              mobileImage: mobileImageUrl,
            };
          });

          formData.append("heroSlides", JSON.stringify(heroSlides));

          payload.heroSlides.forEach((slide, index) => {
            const desktopFile =
              slide.imageFile instanceof File
                ? slide.imageFile
                : slide.image instanceof File
                ? slide.image
                : null;

            const mobileFile =
              slide.mobileImageFile instanceof File
                ? slide.mobileImageFile
                : slide.mobileImage instanceof File
                ? slide.mobileImage
                : null;

            if (desktopFile) {
              formData.append(`heroSlides_${index}_image`, desktopFile);
            }
            if (mobileFile) {
              formData.append(`heroSlides_${index}_mobileImage`, mobileFile);
            }
          });
        }

        /* ======================================
           ABOUT SECTION (WITH COVER & MULTI-IMAGE GALLERY)
        ====================================== */
        if (payload.aboutSection && typeof payload.aboutSection === "object") {
          const aboutImages = Array.isArray(payload.aboutSection.images)
            ? payload.aboutSection.images.filter(
                (img) => typeof img === "string",
              )
            : [];

          const aboutPayload = {
            title: payload.aboutSection.title || "",
            eyebrow: payload.aboutSection.eyebrow || "OUR PURPOSE",
            slug: payload.aboutSection.slug || "",
            code: payload.aboutSection.code || "ABOUT US",
            description: payload.aboutSection.description || "",
            overview: payload.aboutSection.overview || "",
            content: payload.aboutSection.content || "",
            image:
              typeof payload.aboutSection.image === "string"
                ? payload.aboutSection.image
                : "",
            images: aboutImages,
            icon: payload.aboutSection.icon || "fa-school",
            keyFeatures: Array.isArray(payload.aboutSection.keyFeatures)
              ? payload.aboutSection.keyFeatures
              : [],
            highlights: Array.isArray(payload.aboutSection.highlights)
              ? payload.aboutSection.highlights
              : [],
            badgeText: payload.aboutSection.badgeText || "EST. EXCELLENCE",
            buttonText: payload.aboutSection.buttonText || "Read Full Story",
            buttonLink: payload.aboutSection.buttonLink || "/about",
          };

          formData.append("aboutSection", JSON.stringify(aboutPayload));

          // Upload About Cover Image
          const aboutCoverFile =
            payload.aboutSection.imageFile instanceof File
              ? payload.aboutSection.imageFile
              : payload.aboutSection.image instanceof File
              ? payload.aboutSection.image
              : null;

          if (aboutCoverFile) {
            formData.append("aboutSection_image", aboutCoverFile);
          }

          // Upload About Gallery Images
          if (Array.isArray(payload.aboutSection.imagesFiles)) {
            payload.aboutSection.imagesFiles.forEach((file, imgIdx) => {
              if (file instanceof File) {
                formData.append(`aboutSection_images_${imgIdx}`, file);
              }
            });
          }
        }

        /* ======================================
           BOARD & EXECUTIVE OFFICERS (DYNAMIC ARRAY)
        ====================================== */
        if (Array.isArray(payload.executiveOfficers)) {
          const officers = payload.executiveOfficers.map((officer, index) => ({
            _id: officer._id,
            eyebrow: officer.eyebrow || "",
            name: officer.name || "",
            designation: officer.designation || "",
            message: officer.message || "",
            photo: typeof officer.photo === "string" ? officer.photo : "",
            sortOrder:
              officer.sortOrder !== undefined ? officer.sortOrder : index,
          }));

          formData.append("executiveOfficers", JSON.stringify(officers));

          payload.executiveOfficers.forEach((officer, index) => {
            const officerFile =
              officer.file instanceof File
                ? officer.file
                : officer.photo instanceof File
                ? officer.photo
                : officer.imageFile instanceof File
                ? officer.imageFile
                : null;

            if (officerFile) {
              formData.append(`executiveOfficers_${index}_photo`, officerFile);
            }
          });
        }

        /* ======================================
           ADMISSION PROCESS STEPS
        ====================================== */
        if (Array.isArray(payload.admissionSteps)) {
          const admissionSteps = payload.admissionSteps.map((step, index) => ({
            _id: step._id,
            icon: step.icon || "fa-file-pen",
            title: step.title || "",
            description: step.description || "",
            sortOrder: step.sortOrder !== undefined ? step.sortOrder : index,
          }));

          formData.append("admissionSteps", JSON.stringify(admissionSteps));
        }

        /* ======================================
           GALLERY
        ====================================== */
        if (Array.isArray(payload.gallery)) {
          const gallery = payload.gallery.map((item) => ({
            _id: item._id,
            title: item.title || "",
            category: item.category || "",
            image: typeof item.image === "string" ? item.image : "",
          }));

          formData.append("gallery", JSON.stringify(gallery));

          payload.gallery.forEach((item, index) => {
            const file =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (file) {
              formData.append(`gallery_${index}_image`, file);
            }
          });
        }

        /* ======================================
           VIDEOS
        ====================================== */
        if (Array.isArray(payload.videos)) {
          const videos = payload.videos.map((item) => ({
            _id: item._id,
            title: item.title || "",
            youtubeUrl: item.youtubeUrl || "",
            thumbnail: typeof item.thumbnail === "string" ? item.thumbnail : "",
          }));

          formData.append("videos", JSON.stringify(videos));

          payload.videos.forEach((item, index) => {
            const thumbFile =
              item.thumbnailFile instanceof File
                ? item.thumbnailFile
                : item.thumbnail instanceof File
                ? item.thumbnail
                : null;

            if (thumbFile) {
              formData.append(`videos_${index}_thumbnail`, thumbFile);
            }
          });
        }

        /* ======================================
           NEWS
        ====================================== */
        if (Array.isArray(payload.news)) {
          const news = payload.news.map((item) => ({
            _id: item._id,
            title: item.title || "",
            description: item.description || "",
            category: item.category || "General",
            icon: item.icon || "fa-newspaper",
            pinned: Boolean(item.pinned),
            link: item.link || "",
            createdAt: item.createdAt || new Date(),
            image: typeof item.image === "string" ? item.image : "",
          }));

          formData.append("news", JSON.stringify(news));

          payload.news.forEach((item, index) => {
            const imageFile =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (imageFile) {
              formData.append(`news_${index}_image`, imageFile);
            }
          });
        }

        /* ======================================
           UPCOMING CALENDAR WIDGET EVENTS
        ====================================== */
        if (Array.isArray(payload.upcomingEvents)) {
          const upcomingEvents = payload.upcomingEvents.map((ev, index) => ({
            _id: ev._id,
            day: ev.day || "18",
            month: ev.month || "MAY",
            title: ev.title || "",
            locationTime: ev.locationTime || ev.location || "",
            link: ev.link || "",
            sortOrder: ev.sortOrder !== undefined ? ev.sortOrder : index,
            active: ev.active !== false,
          }));

          formData.append("upcomingEvents", JSON.stringify(upcomingEvents));
        }

        /* ======================================
           AFFILIATION & STATUS HIGHLIGHTS
        ====================================== */
        if (Array.isArray(payload.affiliationHighlights)) {
          formData.append(
            "affiliationHighlights",
            JSON.stringify(payload.affiliationHighlights.filter(Boolean)),
          );
        } else if (typeof payload.affiliationHighlights === "string") {
          formData.append(
            "affiliationHighlights",
            payload.affiliationHighlights,
          );
        }

        /* ======================================
           EVENTS
        ====================================== */
        if (Array.isArray(payload.events)) {
          const events = payload.events.map((event) => ({
            _id: event._id,
            title: event.title || "",
            description: event.description || "",
            eventDate: event.eventDate || null,
            location: event.location || "",
            buttonText: event.buttonText || "",
            buttonLink: event.buttonLink || "",
            image: typeof event.image === "string" ? event.image : "",
          }));

          formData.append("events", JSON.stringify(events));

          payload.events.forEach((event, index) => {
            const file =
              event.imageFile instanceof File
                ? event.imageFile
                : event.image instanceof File
                ? event.image
                : null;

            if (file) {
              formData.append(`events_${index}_image`, file);
            }
          });
        }

        /* ======================================
           TESTIMONIALS
        ====================================== */
        if (Array.isArray(payload.testimonials)) {
          const testimonials = payload.testimonials.map((item, index) => ({
            _id: item._id,
            name: item.name || "",
            role: item.role || "Parent",
            quote: item.quote || item.review || "",
            review: item.review || item.quote || "",
            rating: item.rating !== undefined ? item.rating : 5,
            sortOrder: item.sortOrder !== undefined ? item.sortOrder : index,
            active: item.active !== false,
            image: typeof item.image === "string" ? item.image : "",
          }));

          formData.append("testimonials", JSON.stringify(testimonials));

          payload.testimonials.forEach((item, index) => {
            const file =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (file) {
              formData.append(`testimonials_${index}_image`, file);
            }
          });
        }

        /* ======================================
           ACHIEVERS
        ====================================== */
        if (Array.isArray(payload.achievers)) {
          const achievers = payload.achievers.map((item) => ({
            _id: item._id,
            studentName: item.studentName || "",
            achievement: item.achievement || "",
            year: item.year || "",
            image: typeof item.image === "string" ? item.image : "",
          }));

          formData.append("achievers", JSON.stringify(achievers));

          payload.achievers.forEach((item, index) => {
            const file =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (file) {
              formData.append(`achievers_${index}_image`, file);
            }
          });
        }

        /* ======================================
           FACILITIES
        ====================================== */
        if (Array.isArray(payload.facilities)) {
          const facilities = payload.facilities.map((item) => ({
            _id: item._id,
            title: item.title || "",
            description: item.description || "",
            image: typeof item.image === "string" ? item.image : "",
          }));

          formData.append("facilities", JSON.stringify(facilities));

          payload.facilities.forEach((item, index) => {
            const file =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (file) {
              formData.append(`facilities_${index}_image`, file);
            }
          });
        }

        /* ======================================
           INFRASTRUCTURE TABS
        ====================================== */
        if (Array.isArray(payload.infrastructureTabs)) {
          const infrastructureTabs = payload.infrastructureTabs.map((item) => ({
            _id: item._id,
            title: item.title || "",
            image: typeof item.image === "string" ? item.image : "",
          }));

          formData.append(
            "infrastructureTabs",
            JSON.stringify(infrastructureTabs),
          );

          payload.infrastructureTabs.forEach((item, index) => {
            const file =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (file) {
              formData.append(`infrastructureTabs_${index}_image`, file);
            }
          });
        }

        /* ======================================
           PROSPECTUS CONFIGURATION
        ====================================== */
        if (payload.prospectus && typeof payload.prospectus === "object") {
          const prospectusPayload = {
            title: payload.prospectus.title || "Download Prospectus 2026–27",
            description:
              payload.prospectus.description ||
              "Curriculum, admissions and campus life — all in one place.",
            fileUrl:
              typeof payload.prospectus.fileUrl === "string"
                ? payload.prospectus.fileUrl
                : "",
            almanacUrl:
              typeof payload.prospectus.almanacUrl === "string"
                ? payload.prospectus.almanacUrl
                : "",
          };

          formData.append("prospectus", JSON.stringify(prospectusPayload));

          const prospectusFile =
            payload.prospectus.file instanceof File
              ? payload.prospectus.file
              : payload.prospectus.fileUrl instanceof File
              ? payload.prospectus.fileUrl
              : null;

          const almanacFile =
            payload.prospectus.almanacFile instanceof File
              ? payload.prospectus.almanacFile
              : payload.prospectus.almanacUrl instanceof File
              ? payload.prospectus.almanacUrl
              : null;

          if (prospectusFile) {
            formData.append("prospectus_fileUrl", prospectusFile);
          }
          if (almanacFile) {
            formData.append("prospectus_almanacUrl", almanacFile);
          }
        }

        /* ======================================
           FOOTER CONFIGURATION
        ====================================== */
        if (payload.footer && typeof payload.footer === "object") {
          const footerPayload = {
            managementNote: payload.footer.managementNote || "",
            copyrightText: payload.footer.copyrightText || "",
            poweredByText:
              payload.footer.poweredByText || "Powered by Educanium",
            poweredByLink:
              payload.footer.poweredByLink || "https://educanium.com",
            usefulLinks: Array.isArray(payload.footer.usefulLinks)
              ? payload.footer.usefulLinks
              : [],
            academicLinks: Array.isArray(payload.footer.academicLinks)
              ? payload.footer.academicLinks
              : [],
          };

          formData.append("footer", JSON.stringify(footerPayload));
        }

        /* ======================================
           PILLARS (SHAPING BRIGHT FUTURES)
        ====================================== */
        if (Array.isArray(payload.pillars)) {
          const pillars = payload.pillars.map((pillar, index) => ({
            _id: pillar._id,
            num: pillar.num || `${String.fromCharCode(65 + index)}.`,
            icon: pillar.icon || "fa-graduation-cap",
            title: pillar.title || "",
            description: pillar.description || "",
            sortOrder:
              pillar.sortOrder !== undefined ? pillar.sortOrder : index,
            active: pillar.active !== false,
          }));

          formData.append("pillars", JSON.stringify(pillars));
        }

        /* ======================================
           ACADEMIC PROGRAMS
        ====================================== */
        if (Array.isArray(payload.academicPrograms)) {
          const academicPrograms = payload.academicPrograms.map(
            (item, index) => {
              const programImages = Array.isArray(item.images)
                ? item.images.filter((img) => typeof img === "string")
                : [];

              return {
                _id: item._id,
                title: item.title || "",
                slug: item.slug || "",
                code: item.code || "ACADEMIC PROGRAM",
                description: item.description || "",
                overview: item.overview || "",
                content: item.content || "",
                image: typeof item.image === "string" ? item.image : "",
                images: programImages,
                icon: item.icon || "fa-book-open",
                keyFeatures: Array.isArray(item.keyFeatures)
                  ? item.keyFeatures
                  : [],
                highlights: Array.isArray(item.highlights)
                  ? item.highlights
                  : [],
                sortOrder:
                  item.sortOrder !== undefined ? item.sortOrder : index,
                active: item.active !== false,
              };
            },
          );

          formData.append("academicPrograms", JSON.stringify(academicPrograms));

          // Upload cover images & additional media files per program
          payload.academicPrograms.forEach((item, index) => {
            const file =
              item.imageFile instanceof File
                ? item.imageFile
                : item.image instanceof File
                ? item.image
                : null;

            if (file) {
              formData.append(`academicPrograms_${index}_image`, file);
            }

            if (Array.isArray(item.imagesFiles)) {
              item.imagesFiles.forEach((additionalFile, imgIdx) => {
                if (additionalFile instanceof File) {
                  formData.append(
                    `academicPrograms_${index}_images_${imgIdx}`,
                    additionalFile,
                  );
                }
              });
            }
          });
        }

        /* ======================================
           STATS
        ====================================== */
        if (Array.isArray(payload.stats)) {
          const stats = payload.stats.map((item) => ({
            _id: item._id,
            number: item.number || "",
            label: item.label || "",
          }));

          formData.append("stats", JSON.stringify(stats));
        }

        /* ======================================
           SEO KEYWORDS
        ====================================== */
        if (Array.isArray(payload.seoKeywords)) {
          formData.append("seoKeywords", JSON.stringify(payload.seoKeywords));
        } else if (typeof payload.seoKeywords === "string") {
          formData.append("seoKeywords", payload.seoKeywords);
        }

        /* ======================================
           API CALL
        ====================================== */
        const res = await api.put("/api/website/update-website", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Commit updated school object from server
        commit("SET_WEBSITE_DETAILS", res.data.school);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update website:",
          err.response?.data || err.message,
        );
        throw err;
      } finally {
        commit("SET_WEBSITE_SAVING", false);
      }
    },
    // ======================================
    // CREATE WEBSITE
    // ======================================
    async createWebsite({ dispatch }) {
      try {
        const res = await api.post("/api/website/create-website");

        console.log("✅ Website created:", res.data);

        await dispatch("fetchMySchool");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to create website:",
          err.response?.data || err.message,
        );

        throw err;
      }
    },

    // ======================================
    // TOGGLE WEBSITE STATUS
    // ======================================
    async toggleWebsiteStatus({ dispatch }) {
      try {
        const res = await api.patch("/api/website/toggle-status");

        console.log("✅ Website status updated:", res.data);

        await dispatch("fetchMySchool");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to toggle website:",
          err.response?.data || err.message,
        );

        throw err;
      }
    },

    async fetchTeacherAttendanceTiming() {
      try {
        const res = await api.get("/api/attendance/teacher/timing");
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch attendance timing:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },
    async updateTeacherAttendanceTiming(_, { checkInTime, checkOutTime }) {
      try {
        if (!checkInTime || !checkOutTime) {
          throw new Error("checkInTime and checkOutTime are required");
        }

        const res = await api.put("/api/attendance/teacher/update-timing", {
          checkInTime,
          checkOutTime,
        });

        console.log("✅ Teacher attendance timing updated:", res.data);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update attendance timing:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },
    async fetchTeacherYearlyAttendance(
      { commit, state },
      { userId, year, startDate = null, endDate = null },
    ) {
      try {
        if (!userId) throw new Error("User ID required");

        const selectedYear = year || new Date().getFullYear();

        const rangeKey =
          startDate && endDate
            ? `${selectedYear}_${startDate}_${endDate}`
            : `${selectedYear}_full`;

        if (state.teacherAttendanceYearly?.[userId]?.[rangeKey]) {
          return state.teacherAttendanceYearly[userId][rangeKey];
        }

        const params = {
          userId,
          year: selectedYear,
        };

        if (startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        }

        const res = await api.get("/api/attendance/teacher/yearly-summary", {
          params,
        });

        commit("SET_TEACHER_YEARLY_ATTENDANCE", {
          userId,
          rangeKey,
          data: res.data,
        });

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch yearly attendance:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },
    /* ================= ANNOUNCEMENTS ================= */

    async fetchAnnouncements({ commit }) {
      commit("SET_ANNOUNCEMENTS_LOADING", true);
      try {
        const res = await api.get("/api/announcements");

        commit("SET_ANNOUNCEMENTS", res.data || []);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch announcements:",
          err.response?.data || err,
        );
        commit("SET_ANNOUNCEMENTS", []);
      } finally {
        commit("SET_ANNOUNCEMENTS_LOADING", false);
      }
    },

    async createAnnouncement({ commit }, payload) {
      try {
        const res = await api.post("/api/announcements", payload);

        commit("ADD_ANNOUNCEMENT", res.data.announcement);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to create announcement:",
          err.response?.data || err,
        );
        throw err;
      }
    },

    async updateAnnouncement({ commit }, { id, data }) {
      try {
        const res = await api.patch(`/api/announcements/${id}`, data);

        commit("UPDATE_ANNOUNCEMENT", res.data.announcement);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update announcement:",
          err.response?.data || err,
        );
        throw err;
      }
    },

    async deleteAnnouncement({ commit }, id) {
      try {
        await api.delete(`/api/announcements/${id}`);

        commit("DELETE_ANNOUNCEMENT", id);
      } catch (err) {
        console.error(
          "❌ Failed to delete announcement:",
          err.response?.data || err,
        );
        throw err;
      }
    },

    async markAnnouncementRead({ commit }, id) {
      try {
        await api.patch(`/api/announcements/read/${id}`);

        commit("MARK_ANNOUNCEMENT_READ", id);
      } catch (err) {
        console.error("❌ Failed to mark read:", err.response?.data || err);
      }
    },

    async fetchAllSmartCards({ commit }, payload = {}) {
      try {
        const {
          page = 1,
          limit = 10,
          search = "",
          schoolId = null,
          classId = null,
          role = null,
          status = null,
        } = payload;

        const res = await api.get("/api/smartcards", {
          params: {
            page,
            limit,
            search,
            ...(schoolId && { schoolId }),
            ...(classId && { classId }),
            ...(role && { role }),
            ...(status && { status }),
          },
        });

        console.log("✅ Smart cards fetched:", res.data);

        commit("SET_SMART_CARDS", res.data.data || []);

        commit(
          "SET_CARDS_PAGINATION",
          res.data.pagination || {
            page: 1,
            limit,
            total: 0,
            pages: 1,
          },
        );

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch smart cards:",
          err.response?.data || err.message,
        );

        commit("SET_SMART_CARDS", []);

        commit("SET_CARDS_PAGINATION", {
          page: 1,
          limit: 10,
          total: 0,
          pages: 1,
        });

        throw err;
      }
    },

    async fetchSmartCardStats({ commit }) {
      try {
        const res = await api.get("/api/smartcards/stats");

        console.log("✅ SmartCard stats fetched:", res.data);

        if (res.data?.data) {
          commit("SET_SMARTCARD_STATS", res.data.data);
        }

        return res.data.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch SmartCard stats:",
          err.response?.data || err.message,
        );
        return null;
      }
    },

    async bookSmartCard(
      { dispatch },
      { userId, allStudents = false, allTeachers = false, both = false },
    ) {
      try {
        let endpoint = "/api/smartcards/book";
        let config = {};

        if (!userId || (Array.isArray(userId) && userId.length === 0)) {
          const params = new URLSearchParams();

          if (allStudents) params.append("allstudents", "true");
          if (allTeachers) params.append("allteachers", "true");
          if (both) params.append("both", "true");

          const queryString = params.toString();
          if (queryString) endpoint += `?${queryString}`;
        }

        console.log("📤 Sending request to:", endpoint);

        const res = await api.post(endpoint, userId ? { userId } : {}, config);

        console.log("✅ Smart card booked successfully:", res.data);

        if (dispatch) {
          await dispatch("fetchAllSmartCards");
        }

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to book smart card:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async updateDeviceDetails(
      { dispatch },
      { deviceId, label = undefined, deviceName = undefined },
    ) {
      try {
        if (!deviceId) throw new Error("Device ID is required");

        const payload = {};

        if (label !== undefined) payload.label = label;
        if (deviceName !== undefined) payload.deviceName = deviceName;

        if (Object.keys(payload).length === 0) {
          throw new Error("Nothing to update");
        }

        const res = await api.put(`/api/devices/${deviceId}/label`, payload);

        console.log("✅ Device updated:", res.data);

        await dispatch("fetchMyDevices");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update device:",
          err.response?.data || err.message,
        );

        throw err;
      }
    },

    async generateDeviceSetupCode({ dispatch }, deviceId) {
      try {
        const res = await api.post(`/api/devices/generate-code/${deviceId}`);

        console.log("✅ New setup code generated:", res.data);

        await dispatch("fetchMyDevices");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to generate setup code:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async factoryReset(_, { deviceId }) {
      if (!deviceId) throw new Error("Device ID missing");

      try {
        const res = await api.put(`/api/devices/${deviceId}/reset`);

        console.log("✅ Factory reset success:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed factory reset:",
          err.response?.data || err.message,
        );

        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          (typeof err?.response?.data === "string"
            ? err.response.data
            : null) ||
          err.message ||
          "Failed to reset device";

        throw new Error(message);
      }
    },

    async fetchAllSchoolsBasic({ commit }) {
      try {
        const res = await api.get("/api/school/basic");
        console.log("✅ Fetched schools basic:", res.data.schools);

        commit("SET_SCHOOLS_BASIC", res.data.schools);
        return res.data.schools;
      } catch (err) {
        console.error(
          "❌ fetchAllSchoolsBasic error:",
          err.response?.data || err,
        );
        commit("SET_SCHOOLS_BASIC", []);
        throw err;
      }
    },

    async fetchAllDevices(
      { commit },
      { page = 1, limit = 50, search = "", schoolId = "" } = {},
    ) {
      try {
        const res = await api.get("/api/devices/all", {
          params: { page, limit, search, schoolId },
        });

        console.log("✅ Fetched all devices:", res.data);

        commit("SET_ALL_DEVICES", res.data.devices);
        commit("SET_ALL_DEVICES_TOTAL", res.data.total);

        return res.data;
      } catch (err) {
        console.error("❌ fetchAllDevices error:", err.response?.data || err);

        commit("SET_ALL_DEVICES", []);
        commit("SET_ALL_DEVICES_TOTAL", 0);

        throw err;
      }
    },

    async editDevice(_, { deviceId, updates }) {
      try {
        const res = await api.put(`/api/devices/edit/${deviceId}`, updates);
        console.log("✅ Device updated:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update device:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async bookDevices({ dispatch }, { numberOfDevices }) {
      try {
        const res = await api.post("/api/devices/book", {
          numberOfDevices,
        });

        await dispatch("fetchMyDevices");
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to book devices:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchMyDevices({ commit }) {
      try {
        const res = await api.get("/api/devices/my");

        const devices = res.data?.devices || [];
        const serverTime = res.data?.serverTime || null;

        console.log("✅ Fetched my devices:", devices);

        commit("SET_DEVICES", devices);
        commit("SET_DEVICES_TOTAL", devices.length);

        return {
          devices,
          serverTime,
        };
      } catch (err) {
        console.error(
          "❌ Failed to fetch devices:",
          err.response?.data || err.message,
        );

        commit("SET_DEVICES", []);
        commit("SET_DEVICES_TOTAL", 0);
        throw err;
      }
    },

    async initializeApp({ commit, dispatch, state }) {
      if (state.isInitialized) {
        return {
          schoolId: state.schoolId,
          hasClasses: state.classes.some(
            (cls) => cls.name.toLowerCase() !== "passout",
          ),
          classes: state.classes,
        };
      }

      try {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const decoded = jwt_decode(token);
            if (decoded.schoolId) commit("SET_SCHOOL_ID", decoded.schoolId);
            if (decoded.role) commit("SET_USER_ROLE", decoded.role);
            if (decoded.userId) commit("SET_USER_ID", decoded.userId);
            if (decoded.name) commit("SET_USER_NAME", decoded.name);
          } catch (err) {
            console.error("❌ Failed to decode token:", err);
          }
        }

        await dispatch("fetchMySchool");

        if (state.schoolId) {
          await dispatch("fetchAllClasses");
          await dispatch("fetchTotalTeacherCountOnboarding");
          await dispatch("fetchTotalStudentCountOnboarding");
        }
      } catch (err) {
        console.error("❌ App initialization failed:", err);
        throw err;
      } finally {
        commit("SET_INITIALIZED", true);
      }

      return {
        schoolId: state.schoolId,
        hasClasses: state.classes.some(
          (cls) => cls.name.toLowerCase() !== "passout",
        ),
        classes: state.classes,
      };
    },

    async fetchTotalStudentCountOnboarding({ commit }) {
      try {
        const res = await api.get("/api/students/total");
        const totalCount = res.data?.totalStudents || 0;
        commit("SET_ONBOARDING_TOTAL_STUDENTS", totalCount);
      } catch (err) {
        console.error("❌ Failed to fetch total student count:", err);
        commit("SET_ONBOARDING_TOTAL_STUDENTS", 0);
      }
    },

    async fetchTotalTeacherCountOnboarding({ commit }) {
      try {
        const res = await api.get("/api/teachers/total");
        const totalCount = res.data?.totalTeachers || 0;
        commit("SET_ONBOARDING_TOTAL_TEACHERS", totalCount);
      } catch (err) {
        console.error("❌ Failed to fetch total teacher count:", err);
        commit("SET_ONBOARDING_TOTAL_TEACHERS", 0);
      }
    },

    async fetchNotifications({ commit, state }) {
      try {
        const response = await api.get("/api/leave/notifications", {
          headers: {
            Authorization: `Bearer ${state.currentUser?.token}`,
          },
        });

        if (
          response &&
          response.data &&
          Array.isArray(response.data.notifications)
        ) {
          commit("SET_ATTENDANCE_NOTIFICATIONS", response.data.notifications);
        } else {
          console.warn("Invalid notifications response", response);
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    },

    async updateLeaveStatus({ dispatch }, { leaveId, status }) {
      try {
        const res = await api.patch(
          `/api/leave/admin/leaves/${leaveId}/status`,
          { status },
        );

        await dispatch("fetchMyLeaves", { view: "" });
        await dispatch("fetchMyLeaveBalance");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update leave status:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async applyLeave({ dispatch }, leaveData) {
      try {
        const res = await api.post("/api/leave/apply", leaveData);

        await dispatch("fetchMyLeaves", { view: "true" });
        await dispatch("fetchMyLeaveBalance");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to apply leave:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchMyLeaves(
      { commit },
      {
        page = 1,
        limit = 10,
        search = "",
        leave_type = "",
        month = "",
        year = "",
        view = "",
      } = {},
    ) {
      try {
        const params = new URLSearchParams({
          page,
          limit,
          search,
          leave_type,
          month,
          year,
          view,
        });

        const res = await api.get(`/api/leave/my-leaves?${params.toString()}`);
        const { summary, leaves, pagination } = res.data;

        commit("SET_LEAVE_REQUESTS", leaves);
        commit("SET_LEAVE_SUMMARY", summary);

        return { summary, leaves, pagination };
      } catch (err) {
        console.error(
          "❌ Failed to fetch my leaves:",
          err.response?.data || err.message,
        );
        commit("SET_LEAVE_REQUESTS", []);
        commit("SET_LEAVE_SUMMARY", {
          totalLeaves: 0,
          totalApproved: 0,
          totalPending: 0,
          totalDeclined: 0,
        });
        return null;
      }
    },

    async cancelLeave(_, leaveId) {
      try {
        const res = await api.delete(`/api/leave/cancel/${leaveId}`);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to cancel leave:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchMyLeaveBalance({ commit }, year = "") {
      try {
        const res = await api.get("/api/leave/my-balance", {
          params: { year },
        });

        commit("SET_LEAVE_BALANCE", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch leave balance:",
          err.response?.data || err.message,
        );

        commit("SET_LEAVE_BALANCE", null);
        throw err;
      }
    },

    async fetchUserLeaveBalance({ commit, state }, { userId, year } = {}) {
      try {
        if (!userId) throw new Error("User ID is required");

        const selectedYear = year || new Date().getFullYear();

        if (state.userLeaveBalances?.[userId]?.[selectedYear]) {
          return state.userLeaveBalances[userId][selectedYear];
        }

        const res = await api.get(`/api/leave/user-balance/${userId}`, {
          params: { year: selectedYear },
        });

        commit("SET_USER_LEAVE_BALANCE", {
          userId,
          year: selectedYear,
          balance: res.data,
        });

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch user leave balance:",
          err.response?.data || err.message,
        );

        commit("SET_USER_LEAVE_BALANCE", {
          userId,
          year,
          balance: { balances: [] },
        });

        throw err;
      }
    },

    async createOrUpdateLeavePolicy(_, payload) {
      try {
        const res = await api.post("/api/leave/admin/leave-policy", payload);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to create leave policy:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async updateLeaveBalance(
      { dispatch },
      { leaveType, total, userId = null },
    ) {
      try {
        const payload = {
          leaveType,
          total,
          ...(userId && { userId }),
        };

        const res = await api.patch("/api/leave/admin/update-balance", payload);

        await dispatch("fetchMyLeaveBalance");

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update leave balance:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async deleteLeaveType({ dispatch }, leaveType) {
      try {
        const res = await api.delete("/api/leave/delete-leave-type", {
          data: { type: leaveType },
        });

        await dispatch("fetchMyLeaveBalance");

        console.log("✅ Leave type deleted:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to delete leave type:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async editClassById(_, { classId, updates }) {
      try {
        const res = await api.put(
          `/api/classes/updateClass/${classId}`,
          updates,
        );
        console.log("✅ Class updated successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update class:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async deleteClassById(_, classId) {
      try {
        const res = await api.delete(`/api/classes/deleteClass/${classId}`);
        console.log("✅ Class deleted successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to delete class:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async createClass(_, classData) {
      try {
        const res = await api.post("/api/classes/createClass", classData);
        console.log("✅ Class created successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to create class:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchAllClasses({ commit }) {
      try {
        const res = await api.get("/api/classes");
        commit("SET_CLASSES", res.data);
      } catch (err) {
        console.error(
          "❌ Failed to fetch classes:",
          err.response?.data || err.message,
        );
        commit("SET_CLASSES", { classes: [], total: 0 });
      }
    },

    async scanAttendance() {
      try {
        const response = await api.post("/api/attendance/teacher/scan");
        return response.data;
      } catch (error) {
        console.error(
          "❌ Error in scanAttendance:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async fetchAttendanceForToday(
      { commit },
      { page = 1, limit = 10, name = "", date = "" },
    ) {
      try {
        const res = await api.get("/api/attendance/teacher/today", {
          params: { page, limit, name, date },
        });

        commit("SET_ATTENDANCE_DATA", res.data);
      } catch (err) {
        console.error("❌ Failed to fetch today's attendance:", err);
      }
    },

    async fetchAttendanceNotifications({ commit }) {
      const res = await api.get("/api/attendance/teacher/notifications");
      const notifications = res.data.notifications;
      commit("SET_ATTENDANCE_NOTIFICATIONS", notifications);
      return notifications;
    },

    async updateCurrentUserProfile({ commit }, payload) {
      try {
        let res;
        if (payload instanceof FormData) {
          res = await api.put("/api/users/update-profile", payload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          res = await api.put("/api/users/update-profile", payload);
        }

        if (res.data?.photo) {
          commit("SET_USER_PHOTO", res.data.photo);
          commit("SET_CURRENT_USER_DETAILS", {
            ...this.state.currentUserDetails,
            photo: res.data.photo,
          });
        }

        console.log("✅ Profile updated:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update profile:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchCurrentUser({ commit }) {
      try {
        const res = await api.get("/api/users/me");
        commit("SET_CURRENT_USER_DETAILS", res.data.user || res.data);
      } catch (error) {
        console.error("❌ Failed to fetch current user:", error);
      }
    },

    async editTeacherById(_, { teacherId, updates }) {
      try {
        const res = await api.put(`/api/teachers/${teacherId}`, updates);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update teacher:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchTeachers({ commit }, { page = 1, limit = 10, search = "" }) {
      try {
        const res = await api.get("/api/teachers", {
          params: { page, limit, search },
        });

        const { data, totalPages, total, page: currentPage } = res.data;
        const pagination = {
          totalPages,
          totalResults: total,
          currentPage,
        };

        commit("SET_TEACHERS", data);
        commit("SET_TEACHER_PAGINATION", pagination);
      } catch (err) {
        console.error(
          "❌ Failed to fetch teachers:",
          err.response?.data || err.message,
        );
        commit("SET_TEACHERS", []);
        commit("SET_TEACHER_PAGINATION", {
          totalPages: 0,
          totalResults: 0,
          currentPage: 1,
        });
      }
    },

    async addTeacher(_, formData) {
      try {
        const res = await api.post("/api/auth/register", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        console.log("✅ Teacher registered successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to register teacher:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async createSchool({ dispatch }, schoolData) {
      try {
        const res = await api.post("/api/schools", schoolData);

        if (res.data.token) {
          localStorage.setItem("token", res.data.token);
          dispatch("initializeUserRole");
        }

        await dispatch("fetchMySchool");

        return res.data.school;
      } catch (err) {
        console.error(
          "❌ Failed to create school:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async editSchool(_, schoolData) {
      try {
        const res = await api.put("/api/schools", schoolData);
        console.log("✅ School updated successfully:", res.data);
        return res.data.school;
      } catch (err) {
        console.error(
          "❌ Failed to update school:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchMySchool({ commit }) {
      try {
        const res = await api.get("/api/schools");

        if (res.data.school) {
          commit("SET_SCHOOLS", [res.data.school]);
        } else {
          commit("SET_SCHOOLS", []);
        }
      } catch (err) {
        console.error("❌ Failed to fetch school:", err);

        if (err.response && err.response.status === 401) {
          throw err;
        }

        commit("SET_SCHOOLS", []);
      }
    },

    async fetchUpcomingEvents(
      { commit },
      { calendarId, apiKey, timeMin, timeMax },
    ) {
      const url = `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?key=${apiKey}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime`;
      try {
        const res = await fetch(url);
        const data = await res.json();
        commit("setUpcomingEvents", data.items || []);
      } catch (err) {
        console.error("Error fetching calendar events", err);
      }
    },

    async fetchTotalStudentCount({ commit }) {
      try {
        const res = await api.get("/api/students", {
          params: {
            page: 1,
            limit: 1,
          },
        });

        const total = res.data.pagination?.total || 0;
        commit("SET_TOTAL_STUDENT_COUNT", total);
      } catch (err) {
        console.error("❌ Failed to fetch total student count:", err);
        commit("SET_TOTAL_STUDENT_COUNT", 0);
      }
    },

    async fetchUserById({ commit }, userId) {
      try {
        const res = await api.get(`/api/users/${userId}`);
        const user = res.data;
        commit("SET_USER_CACHE", { userId, user });
        commit("SET_CURRENT_USER", user);
        return user;
      } catch (err) {
        console.error("❌ Failed to fetch user:", err);
        return null;
      }
    },

    async fetchStudents({ commit }, filters = {}) {
      try {
        const {
          search = "",
          page = 1,
          limit = 20,
          sort = "desc",
          class: classFilter = "",
          year = "",
          date = "",
        } = filters;
        const res = await api.get("/api/attendance/students", {
          params: {
            search,
            page,
            limit,
            sort,
            class: classFilter,
            year,
            date,
          },
        });

        commit("SET_STUDENTS", res.data.students);
        if (res.data.pagination) {
          commit("SET_STUDENT_PAGINATION", res.data.pagination);
        }
      } catch (err) {
        console.error("❌ Failed to fetch students:", err);
        commit("SET_STUDENTS", []);
      }
    },

    initializeUserRole({ commit }) {
      const token = localStorage.getItem("token");

      if (!token) {
        commit("SET_USER_ROLE", null);
        commit("SET_USER_NAME", null);
        commit("SET_USER_ID", null);
        commit("SET_USER_PHOTO", null);
        commit("SET_SCHOOL_ID", null);
        return;
      }

      try {
        const decoded = jwt_decode(token);
        const { role, name, userId, photo, schoolId } = decoded;

        commit("SET_USER_ROLE", role || null);
        commit("SET_USER_NAME", name || null);
        commit("SET_USER_ID", userId || null);
        commit("SET_USER_PHOTO", photo || null);
        commit("SET_SCHOOL_ID", schoolId || null);
      } catch (error) {
        console.error("Failed to decode token:", error);
        commit("SET_USER_ROLE", null);
        commit("SET_USER_NAME", null);
        commit("SET_USER_ID", null);
        commit("SET_USER_PHOTO", null);
        commit("SET_SCHOOL_ID", null);
      }
    },

    async addStudent(_, studentData) {
      try {
        const res = await api.post("/api/auth/register", studentData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("✅ Student registered successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to register student:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async markAttendance(
      _,
      {
        studentId,
        status,
        subject = "",
        notes = "",
        attendanceByNFC = false,
        date = null,
      },
    ) {
      try {
        const res = await api.post("/api/attendance/manual", {
          studentId,
          status,
          subject,
          notes,
          attendanceByNFC,
          date,
        });

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to mark attendance:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async markAllPresent(_, { classId, date }) {
      try {
        const res = await api.post("/api/attendance/mark-all-present", {
          classId,
          date,
        });

        console.log("✅ All students marked present:", res.data);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to mark all present:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async markAllAbsent(_, { classId, date }) {
      try {
        const res = await api.post("/api/attendance/mark-all-absent", {
          classId,
          date,
        });

        console.log("✅ All students marked absent:", res.data);

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to mark all absent:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchStudentYearlyAttendance(
      { commit, state },
      { userId, year, startDate = null, endDate = null },
    ) {
      try {
        if (!userId) throw new Error("User ID required");

        const selectedYear = year || new Date().getFullYear();

        const rangeKey =
          startDate && endDate
            ? `${selectedYear}_${startDate}_${endDate}`
            : `${selectedYear}_full`;

        if (state.studentAttendanceYearly?.[userId]?.[rangeKey]) {
          return state.studentAttendanceYearly[userId][rangeKey];
        }

        const params = {
          userId,
          year: selectedYear,
        };

        if (startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        }

        const res = await api.get("/api/attendance/yearly/student", { params });

        commit("SET_STUDENT_YEARLY_ATTENDANCE", {
          userId,
          rangeKey,
          data: res.data,
        });

        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to fetch student yearly attendance:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async fetchAttendanceHistory({ commit, dispatch }, filters = {}) {
      try {
        const {
          studentId = "",
          startDate = "",
          endDate = "",
          page,
          limit,
          self = false,
          search = "",
          class: classFilter = "",
        } = filters;

        const res = await api.get("/api/attendance/history", {
          params: {
            studentId,
            startDate,
            endDate,
            page,
            limit,
            self: self ? "true" : "false",
            search,
            class: classFilter,
          },
        });

        const attendance = res.data.attendance || [];
        const pagination = res.data.pagination || {};

        const enriched = await Promise.all(
          attendance.map(async (entry) => {
            if (entry.studentId && typeof entry.studentId === "string") {
              const student = await dispatch("fetchUserById", entry.studentId);
              entry.studentName = student?.name || "Unknown";
            }
            if (entry.teacherId && typeof entry.teacherId === "string") {
              const teacher = await dispatch("fetchUserById", entry.teacherId);
              entry.teacherName = teacher?.name || "Unknown";
            }
            return entry;
          }),
        );

        commit("SET_ATTENDANCE_HISTORY", enriched);
        commit("SET_ATTENDANCE_PAGINATION", pagination);
      } catch (err) {
        console.error("❌ Failed to fetch attendance history:", err);
        commit("SET_ATTENDANCE_HISTORY", []);
      }
    },

    async fetchAttendanceStats(
      { commit },
      { classFilter = null, date = null } = {},
    ) {
      try {
        const params = {};

        if (classFilter !== null) params.class = classFilter;

        if (date) {
          const formattedDate = new Date(date).toISOString().split("T")[0];
          params.date = formattedDate;
        }

        const res = await api.get("/api/attendance/stats", { params });

        commit("SET_ATTENDANCE_STATS", res.data || {});
      } catch (err) {
        console.error("❌ Failed to fetch attendance stats:", err);
        commit("SET_ATTENDANCE_STATS", {});
      }
    },

    async fetchTodaysAttendancePercentage({ commit }) {
      try {
        const res = await api.get("/api/attendance/percentage/today");

        commit("SET_TODAYS_ATTENDANCE_PERCENTAGE", res.data);
      } catch (error) {
        console.error(
          "❌ Failed to fetch today's attendance percentage:",
          error,
        );
        commit("SET_TODAYS_ATTENDANCE_PERCENTAGE", null);
      }
    },

    async editUserById(_, { userId, updates }) {
      try {
        const res = await api.put(`/api/users/${userId}`, updates);

        console.log("✅ User updated successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to update user:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async deleteUserById(_, userId) {
      try {
        const res = await api.delete(`/api/users/${userId}`);

        console.log("✅ User deleted successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to delete user:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },

    async promoteStudentsByIds(_, { studentIds, classId, session, year }) {
      try {
        const payload = { studentIds, classId };

        if (session) payload.session = session;
        if (year) payload.year = year;

        const res = await api.post("/api/users/promote", payload);

        console.log("✅ Students promoted successfully:", res.data);
        return res.data;
      } catch (err) {
        console.error(
          "❌ Failed to promote students:",
          err.response?.data || err.message,
        );
        throw err;
      }
    },
  },
});
